import { periodSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, Pager, errorMessage, primaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';
import { type Message } from './shared';

interface Charge {
  id: string;
  period: string;
  amountMinor: number;
  studentName: string;
  routeName: string;
  invoiceNumber: string;
  invoiceStatus: string;
}
interface ChargeResult {
  charged: number;
  totalMinor: number;
  alreadyCharged: number;
  skippedCancelled: number;
  free: number;
  currency: string;
}

/** The month before the current one, as YYYY-MM: the usual month to bill. */
function lastMonth(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
}

export function BillingTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(lastMonth);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);
  const valid = periodSchema.safeParse(period).success;

  const charges = useQuery({
    queryKey: ['transport', schoolId, 'charges', period, page],
    queryFn: () =>
      apiList<Charge>(`/schools/${schoolId}/transport/charges`, {
        query: { period, page, pageSize: 20 },
      }).then((r) => ({ ...r, currency: (r as unknown as { currency: string }).currency })),
    enabled: valid,
  });
  const charge = useMutation({
    mutationFn: () =>
      api<ChargeResult>(`/schools/${schoolId}/transport/charges`, {
        method: 'POST',
        body: { period },
      }),
    onSuccess: (r) => {
      setMessage({
        kind: 'ok',
        text: t('tr.billed', {
          count: r.charged,
          amount: formatMinor(r.totalMinor, r.currency),
          already: r.alreadyCharged,
          cancelled: r.skippedCancelled,
          free: r.free,
        }),
      });
      void queryClient.invalidateQueries({ queryKey: ['transport', schoolId, 'charges'] });
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const currency = charges.data?.currency ?? 'USD';

  return (
    <section aria-label={t('tr.tab.billing')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-end gap-4">
        <TextField
          type="month"
          label={t('tr.month')}
          value={period}
          hint={t('tr.monthHint')}
          onChange={(e) => {
            setPeriod(e.target.value);
            setPage(1);
          }}
        />
        {can('transport.charge', schoolId) && (
          <button
            type="button"
            className={primaryButton}
            disabled={!valid || charge.isPending}
            onClick={() => {
              setMessage(null);
              charge.mutate();
            }}
          >
            {t('tr.chargeMonth')}
          </button>
        )}
      </div>
      {charges.data?.data.length === 0 && <p className="text-slate-600">{t('tr.noCharges')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {charges.data?.data.map((c) => (
          <li key={c.id} className="flex flex-wrap justify-between gap-2 p-3">
            <span>
              <span className="font-medium">{c.studentName}</span> · {c.routeName}
            </span>
            <span>
              {formatMinor(c.amountMinor, currency)} · {c.invoiceNumber}
              {c.invoiceStatus === 'cancelled' ? ` (${t('tr.cancelled')})` : ''}
            </span>
          </li>
        ))}
      </ul>
      {charges.data && charges.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={charges.data.meta.totalPages} onPage={setPage} />
      )}
    </section>
  );
}
